import { map, MapStore } from "nanostores";

export const CODE_OK = 0;
export const CODE_NETWORK_ERR = 900001;
export const CODE_UNKNOWN = -999998;
export const CODE_CANCELED = -999999;

export enum TaskType {
    EncodeVideo,
    Download,
    LoadFFmpeg,
}

export enum TaskStatus {
    Pending,
    Running,
    Finished,
    Error,
}

export interface TaskStore {
    _timestampAdd: number;
    id: string;
    type: TaskType;
    status: TaskStatus;
    info: string;
    current: number;
    total: number;
    errorCode: number;
}

export interface Task {
    $store: MapStore<TaskStore>;
    start: () => void | Promise<void>;
    cancel: () => void | Promise<void>;
}

export class TaskError extends Error {
    errorCode: number;
    constructor(errorCode: number, message?: string) {
        super();
        this.name = "TaskError";
        this.message = message ?? errorCode.toString();
        this.errorCode = errorCode;
    }
}

export const waitTaskResult = (
    task: Task,
    onProgress: (current: number, total: number) => unknown = (current, total) => {},
) => {
    return new Promise((rs, rj) => {
        task.$store.subscribe((ts, _, changeKey) => {
            if (changeKey && (changeKey !== "status") && (changeKey !== "total") && (changeKey !== "current")) return;
            if (changeKey === "total" || changeKey === "current") {
                onProgress(ts.current, ts.total);
            }
            switch (ts.status) {
                case TaskStatus.Finished:
                    rs();
                    break;
                case TaskStatus.Error:
                    rj(new TaskError(ts.errorCode, ts.info));
                    break;
            }
        });
    }) as Promise<void>;
};

export abstract class AbstractTask implements Task {
    $store: MapStore<TaskStore>;
    #ac = new AbortController();
    constructor(taskType: TaskType, initTotal = 0, initInfo = "") {
        this.$store = map<TaskStore>({
            _timestampAdd: 0,
            id: "",
            type: taskType,
            status: TaskStatus.Pending,
            info: initInfo,
            current: 0,
            total: initTotal,
            errorCode: CODE_OK,
        });
    }
    start() {
        if (this.$store.get().status === TaskStatus.Pending) {
            this.$store.setKey("status", TaskStatus.Running);
            (async () => {
                try {
                    await this._action(this.#ac.signal);
                    this.$store.setKey("status", TaskStatus.Finished);
                } catch (err) {
                    console.error(err);
                    this.$store.setKey("info", err instanceof Error ? err.message : String(err));
                    if (this.#ac.signal.aborted) {
                        this.$store.setKey("errorCode", CODE_CANCELED);
                    } else {
                        this.$store.setKey("errorCode", CODE_UNKNOWN);
                    }
                    this.$store.setKey("status", TaskStatus.Error);
                }
            })();
        }
    }
    cancel() {
        this.#ac.abort();
        this.#ac = new AbortController();
    }
    abstract _action: (abortSignal: AbortSignal) => Promise<unknown>;
}
